import type { VendorDashboardStats } from "@/interfaces";
import api from "./api";
import { CommentItem } from "@/components/approvalStatusTimeline";

/**
 * Finance service for Finance module API calls
 * Supports both QuickBooks and NetSuite providers
 */

// ============================================================================
// PROVIDER TYPE
// ============================================================================

export type FinanceProvider = "quickbooks" | "netsuite";

// ============================================================================
// DASHBOARD STATS TYPES
// ============================================================================

export interface FinanceDashboardStats {
  totalCompanies: number;
  hasQuickBooks: boolean;
  hasNetSuite: boolean;
  connectedToQuickBooks: number;
  connectedToNetSuite: number;
  connectedToStripe: number;
  quickbooks: FinanceProviderDashboardStats | null;
  netsuite: FinanceProviderDashboardStats | null;
}

export interface FinanceProviderMonthlyStats {
  totalInvoices: number;
  totalInvoiceAmount: number;
  pendingInvoices: number;
  pendingInvoiceAmount: number;
  paidInvoices: number;
}

export interface FinanceProviderDashboardStats {
  totalInvoices: number;
  totalInvoiceAmount: number;
  approvedInvoices: number;
  pendingApprovals: number;
  rejectedInvoices: number;
  pendingInvoices: number;
  pendingInvoiceAmount: number;
  paidInvoices: number;
  totalPayments: number;
  totalPaymentRevenue: number;
  succeededPayments: number;
  pendingPayments: number;
  
  currentMonth?: FinanceProviderMonthlyStats;
  previousMonth?: FinanceProviderMonthlyStats;

  recentPendingInvoices: FinanceDashboardPendingInvoiceItem[];
  recentPendingApprovals: FinanceDashboardPendingApprovalItem[];
}

export interface FinanceDashboardPendingInvoiceItem {
  id: string;
  invoiceNumber: string;
  clientName: string;
  companyId: string;
  companyName: string;
  balance: number;
  dueDate: string | null;
  status: "Open" | "Overdue";
}

export interface FinanceDashboardPendingApprovalItem {
  id: string;
  invoiceNumber: string | null;
  clientName: string;
  companyId: string;
  companyName: string;
  amount: number;
  status: string;
  submittedBy: string;
  submittedDate: string;
  priority: string;
}

/**
 * Get all finance dashboard stats in a single API call
 * Returns: totalInvoices, pendingInvoices, totalCompanies, totalRevenue
 */
export const getFinanceDashboardStats = async (): Promise<FinanceDashboardStats> => {
  const response = await api.get("/company/finance-dashboard-stats");
  return response.data.data;
};

// ============================================================================
// INVOICE TYPES & API
// ============================================================================

export interface FinanceInvoiceItem {
  id: string;
  invoiceNumber: string;
  clientName: string;
  clientId?: string | null;
  clientEmail: string | null;
  companyId: string;
  companyName: string;
  currency?: string;
  totalAmount: number;
  balance: number;
  dueDate: string | null;
  txnDate: string | null;
  status: "Paid" | "Open" | "Overdue";
  saleType?: "New Sale" | "Upsell" | "Resale" | "Cross Sale" | "Refund" | null;
  lastSyncTime?: string | null;
}

// ============================================================================
// INVOICE STATS TYPES & API
// ============================================================================

export interface InvoiceStats {
  totalInvoices: number;
  paidCount: number;
  openCount: number;
  overdueCount: number;
  totalAmount: number;
  companies: Array<{ id: string; name: string }>;
}

/**
 * Get invoice stats for all assigned companies (no filters)
 * Call once and memoize the result
 * @param provider - "quickbooks" or "netsuite"
 */
export const getInvoiceStats = async (provider: FinanceProvider = "quickbooks"): Promise<InvoiceStats> => {
  if (provider === "netsuite") {
    return {
      totalInvoices: 0,
      paidCount: 0,
      openCount: 0,
      overdueCount: 0,
      totalAmount: 0,
      companies: [],
    };
  }

  const response = await api.get("/quickbooks/invoices/assigned-companies/stats");
  return response.data.data;
};

// ============================================================================
// INVOICE LIST TYPES & API
// ============================================================================

export interface GetInvoicesParams {
  status?: "all" | "pending" | "open" | "paid" | "overdue";
  // Server-side pagination params
  page?: number;
  pageSize?: number;
  // Server-side filter params
  companyId?: string;
  search?: string;
  dueDateStart?: string;
  dueDateEnd?: string;
  txnDateStart?: string;
  txnDateEnd?: string;
  amountMin?: number;
  amountMax?: number;
}

export interface GetInvoicesResponse {
  data: FinanceInvoiceItem[];
  total: number;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  companies: Array<{ id: string; name: string }>;
}

/**
 * Get invoices from all of user's assigned companies
 * Supports filtering by status, limit, and sorting
 * @param provider - "quickbooks" or "netsuite"
 * @param params - Optional filter parameters
 */
export const getInvoicesForAssignedCompanies = async (
  provider: FinanceProvider = "quickbooks",
  params?: GetInvoicesParams,
): Promise<GetInvoicesResponse> => {
  const requestParams: GetInvoicesParams = { ...(params || {}) };
  delete requestParams.page;
  delete requestParams.pageSize;

  if (provider === "netsuite") {
    const response = await api.get("/netsuite/invoices/assigned-companies", {
      params: requestParams,
    });
    return response.data;
  }

  const response = await api.get("/quickbooks/invoices/assigned-companies", {
    params: requestParams,
  });
  return response.data;
};

/**
 * Get recent pending invoices for dashboard
 * Convenience wrapper that calls getInvoicesForAssignedCompanies with pending filter
 * @param provider - "quickbooks" or "netsuite"
 * @param limit - Number of invoices to return
 */
export const getRecentPendingInvoices = async (
  provider: FinanceProvider = "quickbooks",
  limit: number = 5,
): Promise<{ data: FinanceInvoiceItem[]; total: number }> => {
  const response = await getInvoicesForAssignedCompanies(provider, {
    status: "pending",
    pageSize: limit,
  });
  console.log("Recent Pending Invoices Response:", response);
  return { data: response.data, total: response.total };
};

// ============================================================================
// APPROVAL TYPES & API
// ============================================================================

/**
 * Line item in an invoice request
 */
export interface InvoiceRequestItem {
  productService: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

/**
 * Full invoice request data stored in approval record
 */
export interface InvoiceRequestData {
  clientId?: string;
  clientName?: string;
  clientEmail?: string;
  issueDate?: string;
  dueDate?: string;
  serviceStartDate?: string;
  serviceEndDate?: string;
  saleType?: string;
  items?: InvoiceRequestItem[];
  notes?: string;
  vendorBillLink?: string;
}

export interface FinanceApprovalItem {
  id: string;
  invoiceNumber: string | null;
  clientName: string;
  companyId: string;
  companyName: string;
  currency: string;
  amount: number;
  status: string;
  submittedBy: string;
  submittedDate: string;
  approvedBy: string | null;
  approvedDate: string | null;
  rejectionReason: string | null;
  priority: string;
  isRecurring?: boolean;
  invoiceRequest: InvoiceRequestData | null;
  taxTotal?: number | null;
}

// ============================================================================
// APPROVAL STATS TYPES & API
// ============================================================================

export interface ApprovalStats {
  totalApprovals: number;
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
  companies: Array<{ id: string; name: string }>;
}

/**
 * Get approval stats for all assigned companies (no filters)
 * Call once and memoize the result
 * @param provider - "quickbooks" or "netsuite"
 */
export const getApprovalStats = async (provider: FinanceProvider = "quickbooks", companyId?: string | undefined): Promise<ApprovalStats> => {
  const response = await api.get("/invoices/assigned-companies/approvals/stats", {
    params: { provider, companyId },
  });
  return response.data.data;
};

// ============================================================================
// APPROVAL LIST TYPES & API
// ============================================================================

export interface GetApprovalsParams {
  status?: "Pending" | "Approved" | "Rejected" | "Auto-Approved";
  priority?: "High" | "Medium" | "Low";
  isRecurring?: "true" | "false";
  // Server-side pagination params
  page?: number;
  pageSize?: number;
  // Server-side filter params
  companyId?: string;
  search?: string;
  submittedDateStart?: string;
  submittedDateEnd?: string;
  amountMin?: number;
  amountMax?: number;
}

export interface GetApprovalsResponse {
  data: FinanceApprovalItem[];
  total: number;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  companies: Array<{ id: string; name: string }>;
}

/**
 * Get approvals for user's assigned companies
 * Supports server-side pagination and filtering
 * @param provider - "quickbooks" or "netsuite"
 * @param params - Optional filter parameters
 */
export const getApprovalsForAssignedCompanies = async (
  provider: FinanceProvider = "quickbooks",
  params?: GetApprovalsParams,
): Promise<GetApprovalsResponse> => {
  const requestParams = {
    ...params,
    provider,
  };

  const response = await api.get("/invoices/assigned-companies/approvals", {
    params: requestParams,
  });
  return response.data;
};

/**
 * Get recent pending approvals for dashboard
 * Convenience wrapper
 * @param provider - "quickbooks" or "netsuite"
 * @param limit - Number of approvals to return
 */
export const getRecentPendingApprovals = async (
  provider: FinanceProvider = "quickbooks",
  limit: number = 5,
): Promise<{ data: FinanceApprovalItem[]; total: number }> => {
  return getApprovalsForAssignedCompanies(provider, { status: "Pending", pageSize: limit });
};

/**
 * Approve an invoice
 * Creates the invoice in QuickBooks and updates status to Approved
 * @param approvalId - The ID of the invoice approval record
 * @param data - Optional updated invoice data if editing during approval
 */
export const approveInvoice = async (approvalId: string, data?: any): Promise<{ message: string; data: any }> => {
  const response = await api.post(`/invoices/${approvalId}/approve`, data);
  return response.data;
};

/**
 * Reject an invoice
 * Updates status to Rejected with reason
 * @param approvalId - The ID of the invoice approval record
 * @param reason - The rejection reason
 */
export const rejectInvoice = async (approvalId: string, reason: string): Promise<{ message: string; data: any }> => {
  const response = await api.post(`/invoices/${approvalId}/reject`, { reason });
  return response.data;
};

// ============================================================================
// RECEIVABLES TYPES & API
// ============================================================================

export interface ReceivablesItem {
  id: string;
  invoiceNumber: string;
  clientName: string;
  clientEmail: string | null;
  companyId: string;
  companyName: string;
  currency: string;
  totalAmount: number;
  amountReceived: number;
  amountDue: number;
  dueDate: string | null;
  txnDate: string | null;
  daysOverdue: number;
  status: "Paid" | "Open" | "Overdue";
}

export interface ReceivablesSummary {
  totalInvoices: number;
  totalReceived: number;
  outstanding: number;
}

export interface AgingAnalysis {
  current: { amount: number; count: number };
  days31to60: { amount: number; count: number };
  days61to90: { amount: number; count: number };
  over90: { amount: number; count: number };
}

// ============================================================================
// RECEIVABLES STATS TYPES & API
// ============================================================================

export interface ReceivablesStats {
  summary: ReceivablesSummary;
  agingAnalysis: AgingAnalysis;
  companies: Array<{ id: string; name: string }>;
}

/**
 * Get receivables stats for all assigned companies (no filters)
 * Call once and memoize the result
 * @param provider - "quickbooks" or "netsuite"
 */
export const getReceivablesStats = async (provider: FinanceProvider = "quickbooks"): Promise<ReceivablesStats> => {
  if (provider === "netsuite") {
    return {
      summary: { totalInvoices: 0, totalReceived: 0, outstanding: 0 },
      agingAnalysis: {
        current: { amount: 0, count: 0 },
        days31to60: { amount: 0, count: 0 },
        days61to90: { amount: 0, count: 0 },
        over90: { amount: 0, count: 0 },
      },
      companies: [],
    };
  }

  const response = await api.get("/quickbooks/receivables/assigned-companies/stats");
  return response.data.data;
};

// ============================================================================
// RECEIVABLES LIST TYPES & API
// ============================================================================

export interface GetReceivablesParams {
  aging?: "current" | "31-60" | "61-90" | "over90";
  // Server-side pagination params
  page?: number;
  pageSize?: number;
  // Server-side filter params
  companyId?: string;
  search?: string;
  dueDateStart?: string;
  dueDateEnd?: string;
  amountMin?: number;
  amountMax?: number;
}

export interface GetReceivablesResponse {
  data: ReceivablesItem[];
  total: number;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  companies: Array<{ id: string; name: string }>;
}

/**
 * Get receivables (accounts receivable aging) from all of user's assigned companies
 * Supports server-side pagination and filtering
 * @param provider - "quickbooks" or "netsuite"
 * @param params - Optional filter parameters
 */
export const getReceivablesForAssignedCompanies = async (
  provider: FinanceProvider = "quickbooks",
  params?: GetReceivablesParams,
): Promise<GetReceivablesResponse> => {
  if (provider === "netsuite") {
    console.log("[NetSuite] getReceivablesForAssignedCompanies called - API not ready", { params });
    return {
      data: [],
      total: 0,
      pagination: { total: 0, page: 1, pageSize: 10, totalPages: 0 },
      companies: [],
    };
  }

  const response = await api.get("/quickbooks/receivables/assigned-companies", {
    params,
  });
  return response.data;
};

// ============================================================================
// INVOICE DETAILS TYPES & API
// ============================================================================

/**
 * QuickBooks line item in an invoice
 */
export interface QuickBooksLineItem {
  Id?: string;
  LineNum?: number;
  Description?: string;
  Amount: number;
  DetailType: string;
  SalesItemLineDetail?: {
    ItemRef?: {
      value: string;
      name: string;
    };
    Qty?: number;
    UnitPrice?: number;
  };
}

/**
 * Full QuickBooks invoice with line items
 */
export interface QuickBooksInvoiceDetails {
  Id: string;
  DocNumber?: string;
  TxnDate?: string;
  DueDate?: string;
  TotalAmt: number;
  Balance: number;
  CustomerRef?: {
    value: string;
    name: string;
  };
  BillEmail?: {
    Address: string;
  };
  Line?: QuickBooksLineItem[];
  CustomerMemo?: {
    value: string;
  };
  PrivateNote?: string;
}

/**
 * Get a single invoice with full details (including line items) from QuickBooks
 * @param invoiceId - QuickBooks invoice ID
 * @param companyId - Company UUID
 */
export const getQuickBooksInvoiceDetails = async (
  invoiceId: string,
  companyId: string,
): Promise<QuickBooksInvoiceDetails> => {
  const response = await api.get("/quickbooks/invoice", {
    params: { invoiceId, companyId },
  });
  return response.data.data;
};

// ============================================================================
// VENDOR BILLS TYPES & API
// ============================================================================

export interface VendorBillItem {
  id: string;
  transactionnumber: string; // Vendor Invoice Number
  vendorId: string;
  vendorName: string;
  companyId: string;
  companyName: string;
  total: number;
  status: "Paid" | "Open" | "Overdue";
  trandate: string | null;
  duedate: string | null;
  currency: string;
}

export interface GetVendorBillsResponse {
  data: VendorBillItem[];
  total: number;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  companies: Array<{ id: string; name: string }>;
}

/**
 * Get vendor bills from all of user's assigned companies
 */
export const getVendorBillsForAssignedCompanies = async (
  params?: GetInvoicesParams,
): Promise<GetVendorBillsResponse> => {
  const requestParams = { ...(params || {}) };
  delete requestParams.page;
  delete requestParams.pageSize;

  const response = await api.get("/netsuite/vendor-bills/assigned-companies", {
    params: requestParams,
  });
  return response.data;
};

// ============================================================================
// LOCAL VENDOR BILLS & APPROVALS API
// ============================================================================

export interface LocalVendorBillItem {
  id: string;
  companyId: string;
  netSuiteBillId: string;
  vendorName: string;
  transactionNumber: string | null;
  tranid: string;
  trandate: string | null;
  duedate: string | null;
  total: number;
  status: "pending" | "under process" | "approved" | "paid";
  approvedByIds: string[];
  paidById: string | null;
  createdAt: string;
  updatedAt: string;
  companyName?: string;
  approvers: CommentItem[];
  paidByName?: string | null;
  currency?: string;
}

export const startPaymentProcess = async (
  companyId: string,
  netSuiteBillId: string,
  poIds?: string[],
): Promise<{ message: string; data: LocalVendorBillItem; autoApprovedNoApprovers?: boolean }> => {
  const response = await api.post("/vendor-bills/start-payment", { companyId, netSuiteBillId, poIds });
  return response.data;
};

export const getLocalVendorBills = async (companyId?: string): Promise<{ message: string; data: LocalVendorBillItem[] }> => {
  const response = await api.get("/vendor-bills/local", { params: { companyId } });
  return response.data;
};

export const approveVendorBill = async (id: string, comment?: string): Promise<{ message: string; data: LocalVendorBillItem }> => {
  const response = await api.post(`/vendor-bills/${id}/approve`, {
    comment,
  });
  return response.data;
};

export const markVendorBillAsPaid = async (id: string): Promise<{ message: string; data: LocalVendorBillItem }> => {
  const response = await api.post(`/vendor-bills/${id}/mark-paid`);
  return response.data;
};

export const downloadVendorBillPdf = async (companyId: string, billId: string): Promise<Blob> => {
  const response = await api.get("/netsuite/vendor-bill/pdf", {
    params: { companyId, billId },
    responseType: "blob",
  });
  return response.data;
};

export const downloadVendorBillAttachment = async (companyId: string, fileId: string): Promise<Blob> => {
  const response = await api.get("/netsuite/vendor-bill/attachment", {
    params: { companyId, fileId },
    responseType: "blob",
  });
  return response.data;
};

// ============================================================================
// VENDOR DASHBOARD STATS
// ============================================================================

export const getVendorDashboardStats = async (
  companyId?: string,
): Promise<{ message: string; data: VendorDashboardStats }> => {
  const response = await api.get("/vendor-bills/dashboard-stats", {
    params: companyId ? { companyId } : undefined,
  });
  return response.data;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  getFinanceDashboardStats,
  getInvoiceStats,
  getInvoicesForAssignedCompanies,
  getRecentPendingInvoices,
  getApprovalStats,
  getApprovalsForAssignedCompanies,
  getRecentPendingApprovals,
  approveInvoice,
  rejectInvoice,
  getReceivablesStats,
  getReceivablesForAssignedCompanies,
  getQuickBooksInvoiceDetails,
  getVendorBillsForAssignedCompanies,
  startPaymentProcess,
  getLocalVendorBills,
  approveVendorBill,
  markVendorBillAsPaid,
  downloadVendorBillPdf,
  downloadVendorBillAttachment,
  getVendorDashboardStats,
};
